<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * LaisRunStatusPanel — execution panel for jobs, imports, cadences and
 * Captain runs. Purely presentational: the domain passes normalized data and
 * this panel maps the run status to a canonical LAIS tone (via LaisStatusBadge)
 * and lays out timing, attempts, worker/lock, progress, error and next steps.
 *
 * Unknown/unrecognized statuses fall back to the neutral tone so a backend
 * state the UI does not know about is never rendered as success.
 */
const props = defineProps({
  // Accept any string: unknown backend run states map to the neutral tone
  // internally. A strict enum validator would warn on real backend values.
  status: { type: String, default: '' },
  startedAt: { type: [String, Number, Date], default: null },
  finishedAt: { type: [String, Number, Date], default: null },
  durationMs: { type: Number, default: null },
  attempt: { type: Number, default: null },
  maxAttempts: { type: Number, default: null },
  lock: { type: [String, Object], default: null },
  worker: { type: String, default: '' },
  error: { type: String, default: '' },
  nextSteps: { type: Array, default: () => [] },
  progress: {
    type: Number,
    default: null,
    validator: value => value === null || (value >= 0 && value <= 100),
  },
  stale: { type: Boolean, default: false },
});

const KNOWN_STATUSES = Object.freeze([
  'running',
  'completed',
  'failed',
  'queued',
  'stale',
  'cancelled',
  'waiting_approval',
]);

const STATUS_TONE = Object.freeze({
  running: 'info',
  queued: 'info',
  waiting_approval: 'info',
  completed: 'positive',
  failed: 'danger',
  stale: 'warning',
  cancelled: 'neutral',
  unknown: 'neutral',
});

const STATUS_ICON = Object.freeze({
  running: 'i-lucide-loader-circle animate-spin',
  queued: 'i-lucide-clock',
  waiting_approval: 'i-lucide-user-check',
  completed: 'i-lucide-check',
  failed: 'i-lucide-x',
  stale: 'i-lucide-activity',
  cancelled: 'i-lucide-ban',
  unknown: 'i-lucide-circle-help',
});

const EMPTY_VALUE = '—';

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-run-status-${instance?.uid || 'default'}`;
const errorTitleId = `${baseId}-error`;
const stepsId = `${baseId}-steps`;

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
});

const toDate = value => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = value => {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : '';
};

const formatDuration = ms => {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
};

const statusKey = computed(() =>
  KNOWN_STATUSES.includes(props.status) ? props.status : 'unknown'
);
const statusTone = computed(() => STATUS_TONE[statusKey.value] || 'neutral');
const statusIcon = computed(() => STATUS_ICON[statusKey.value]);
// Literal keys (not an interpolated path) keep the i18n extractor able to see
// every status string and avoid dynamic-key lint warnings.
const STATUS_LABEL = Object.freeze({
  running: () => t('LAIS_RUN_STATUS_PANEL.STATUS.running'),
  queued: () => t('LAIS_RUN_STATUS_PANEL.STATUS.queued'),
  waiting_approval: () => t('LAIS_RUN_STATUS_PANEL.STATUS.waiting_approval'),
  completed: () => t('LAIS_RUN_STATUS_PANEL.STATUS.completed'),
  failed: () => t('LAIS_RUN_STATUS_PANEL.STATUS.failed'),
  stale: () => t('LAIS_RUN_STATUS_PANEL.STATUS.stale'),
  cancelled: () => t('LAIS_RUN_STATUS_PANEL.STATUS.cancelled'),
  unknown: () => t('LAIS_RUN_STATUS_PANEL.STATUS.unknown'),
});
const statusLabel = computed(() => STATUS_LABEL[statusKey.value]());

const isRunning = computed(() => statusKey.value === 'running');
const isStale = computed(() => props.stale || statusKey.value === 'stale');

const resolvedDurationMs = computed(() => {
  if (props.durationMs !== null && !Number.isNaN(props.durationMs)) {
    return props.durationMs;
  }
  const start = toDate(props.startedAt);
  const end = toDate(props.finishedAt);
  return start && end ? end.getTime() - start.getTime() : null;
});
const durationDisplay = computed(() =>
  formatDuration(resolvedDurationMs.value)
);

const attemptDisplay = computed(() => {
  if (props.attempt === null || props.attempt === undefined) return '';
  return props.maxAttempts !== null && props.maxAttempts !== undefined
    ? `${props.attempt}/${props.maxAttempts}`
    : String(props.attempt);
});

const lockDisplay = computed(() => {
  const lock = props.lock;
  if (lock === null || lock === undefined || lock === '') return '';
  if (typeof lock === 'string') return lock;
  return Object.entries(lock)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');
});

const field = (key, label, raw) => {
  const value = raw === null || raw === undefined || raw === '' ? '' : raw;
  return {
    key,
    label,
    value: value === '' ? EMPTY_VALUE : value,
    empty: value === '',
  };
};

const gridRows = computed(() => [
  field(
    'started',
    t('LAIS_RUN_STATUS_PANEL.STARTED'),
    formatDateTime(props.startedAt)
  ),
  field(
    'finished',
    t('LAIS_RUN_STATUS_PANEL.FINISHED'),
    formatDateTime(props.finishedAt)
  ),
  field('attempt', t('LAIS_RUN_STATUS_PANEL.ATTEMPT'), attemptDisplay.value),
  field('worker', t('LAIS_RUN_STATUS_PANEL.WORKER'), props.worker),
  field('lock', t('LAIS_RUN_STATUS_PANEL.LOCK'), lockDisplay.value),
]);

const clampedProgress = computed(() =>
  Math.min(100, Math.max(0, props.progress ?? 0))
);
const showProgress = computed(
  () =>
    props.progress !== null && props.progress !== undefined && isRunning.value
);
const progressLabel = computed(() => `${clampedProgress.value}%`);

const hasNextSteps = computed(
  () => Array.isArray(props.nextSteps) && props.nextSteps.length > 0
);
</script>

<template>
  <section
    role="group"
    :aria-label="t('LAIS_RUN_STATUS_PANEL.REGION_LABEL')"
    class="flex flex-col gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 text-lais-text shadow-lais-card"
  >
    <header class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <div class="flex flex-wrap items-center gap-2">
        <LaisStatusBadge
          :label="statusLabel"
          :tone="statusTone"
          :icon="statusIcon"
        />
        <LaisStatusBadge
          v-if="isStale"
          size="sm"
          tone="warning"
          :label="t('LAIS_RUN_STATUS_PANEL.STALE_CHIP')"
          icon="i-lucide-activity"
        />
      </div>
      <span
        v-if="durationDisplay"
        class="inline-flex items-center gap-1.5 text-lais-body-sm text-lais-text-helper"
      >
        <span class="i-lucide-timer size-4 shrink-0" aria-hidden="true" />
        {{ durationDisplay }}
      </span>
    </header>

    <div v-if="showProgress">
      <div
        class="mb-1 flex items-center justify-between text-lais-label-sm font-medium text-lais-text-body"
      >
        <span>{{ t('LAIS_RUN_STATUS_PANEL.PROGRESS') }}</span>
        <span>{{ progressLabel }}</span>
      </div>
      <div
        class="h-1.5 w-full overflow-hidden rounded-full bg-lais-bg-soft"
        role="progressbar"
        :aria-valuenow="clampedProgress"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="t('LAIS_RUN_STATUS_PANEL.PROGRESS')"
      >
        <div
          class="h-full rounded-full bg-lais-primary transition-[width]"
          :style="{ width: `${clampedProgress}%` }"
        />
      </div>
    </div>

    <dl class="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      <div v-for="row in gridRows" :key="row.key" class="flex flex-col gap-0.5">
        <dt class="text-lais-label-sm font-medium text-lais-text-body">
          {{ row.label }}
        </dt>
        <dd
          class="break-words text-lais-body-sm font-semibold"
          :class="row.empty ? 'text-lais-text-empty' : 'text-lais-text-strong'"
        >
          {{ row.value }}
        </dd>
      </div>
    </dl>

    <div
      v-if="error"
      data-testid="lais-run-error"
      role="note"
      :aria-labelledby="errorTitleId"
      class="flex gap-2 rounded-lais border border-lais-danger/40 bg-lais-danger-50 p-3 text-lais-danger"
    >
      <span
        class="i-lucide-octagon-alert mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p
          :id="errorTitleId"
          class="text-lais-label-sm font-semibold uppercase"
        >
          {{ t('LAIS_RUN_STATUS_PANEL.ERROR_TITLE') }}
        </p>
        <p class="mt-1 break-words text-lais-body-sm">{{ error }}</p>
      </div>
    </div>

    <div v-if="hasNextSteps">
      <p
        :id="stepsId"
        class="text-lais-label-sm font-semibold uppercase text-lais-text-body"
      >
        {{ t('LAIS_RUN_STATUS_PANEL.NEXT_STEPS') }}
      </p>
      <ul
        :aria-labelledby="stepsId"
        class="mt-1.5 list-disc space-y-1 pl-5 text-lais-body-sm text-lais-text-body"
      >
        <li
          v-for="(step, index) in nextSteps"
          :key="`step-${index}`"
          class="break-words"
        >
          {{ step }}
        </li>
      </ul>
    </div>
  </section>
</template>
